/**
 * BANK-IBAN-R3.10 — never silently render an obsolete Customer checkout.
 */
import type { CustomerBootstrapPayload } from "./customer-runtime-bootstrap-v2.js";

export const CLIENT_APP_REV = String(import.meta.env.VITE_APP_REV ?? "").trim();
const HANDSHAKE_KEY = "asiastyle.r310.handshake.nonce";

export type RuntimeHandshakeResult = "ok" | "redirecting" | "blocked";

export function enforceCustomerRuntimeHandshake(
  boot: CustomerBootstrapPayload,
  loc: Location = window.location,
): RuntimeHandshakeResult {
  const runtime = boot.customer_runtime;
  if (!runtime?.expected_app_rev || !runtime.canonical_origin) return "ok";
  const origin = loc.origin.replace(/\/$/, "");
  const canonical = runtime.canonical_origin.replace(/\/$/, "");
  const originHost = origin.replace(/^https?:\/\//i, "").toLowerCase();
  const canonicalHost = canonical.replace(/^https?:\/\//i, "").split("/")[0]?.toLowerCase() ?? "";
  const isProductionCustomerHost =
    !originHost.includes("staging") &&
    !originHost.includes("manager") &&
    (originHost === "production.asiastyle-mini-app.pages.dev" ||
      originHost === "app.asiastyle.com.ua" ||
      (originHost.endsWith(".asiastyle-mini-app.pages.dev") &&
        originHost !== "asiastyle-mini-app.pages.dev"));
  const canonicalLooksStaging =
    canonicalHost.includes("staging") ||
    canonicalHost.startsWith("019e9a93.") ||
    canonicalHost === "asiastyle-mini-app.pages.dev";
  // Never bounce a live production unique/alias host onto staging Worker handshake.
  if (isProductionCustomerHost && canonicalLooksStaging) {
    try {
      sessionStorage.removeItem(HANDSHAKE_KEY);
    } catch {
      /* ignore */
    }
    return "ok";
  }
  // Production alias/unique is the live store. Do not block customers during a
  // Worker/Pages rev skew; Telegram is pinned to the production alias.
  if (isProductionCustomerHost) {
    try {
      sessionStorage.removeItem(HANDSHAKE_KEY);
    } catch {
      /* ignore */
    }
    return "ok";
  }
  if (CLIENT_APP_REV && CLIENT_APP_REV === runtime.expected_app_rev && origin === canonical) {
    try {
      sessionStorage.removeItem(HANDSHAKE_KEY);
    } catch {
      /* ignore */
    }
    return "ok";
  }

  let already = "";
  try {
    already = sessionStorage.getItem(HANDSHAKE_KEY) ?? "";
  } catch {
    already = "";
  }
  if (already === runtime.expected_app_rev) return "blocked";

  try {
    sessionStorage.setItem(HANDSHAKE_KEY, runtime.expected_app_rev);
  } catch {
    /* ignore */
  }

  const path = loc.pathname.includes("staging-store-v2") ? "/staging-store-v2/" : loc.pathname || "/";
  const next = new URL(runtime.canonical_origin.replace(/\/$/, "") + path);
  next.searchParams.set("app_rev", runtime.expected_app_rev);
  next.searchParams.set("build", boot.customer_build_id || "COMMERCE-LAUNCH-163A-R2C-RC1");
  next.searchParams.set("cache_nonce", String(Date.now()));
  const owner = new URLSearchParams(loc.search).get("owner_tg");
  if (owner && path.includes("staging-store-v2")) next.searchParams.set("owner_tg", owner);
  const start = new URLSearchParams(loc.search).get("tgWebAppStartParam");
  if (start) next.searchParams.set("tgWebAppStartParam", start);
  loc.replace(next.toString());
  return "redirecting";
}
